import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import LmLoader from "./Lm-Loader";
import Loading from "../beta/components/helpers/Loading";
import { request, requestWithHeaders } from "../beta/utils/fetchData";
import { checkForXSS, safeRedirect } from "../beta/utils/queryStringHelper";

const getTwoFaContextFromQuery = () => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
        returnUrl:
            urlParams.get("returnUrl") ?? urlParams.get("ReturnUrl") ?? null,
        rememberMe:
            urlParams.get("rememberMe") ?? urlParams.get("RememberMe") ?? null,
        provider:
            urlParams.get("provider") ?? urlParams.get("Provider") ?? null,
    };
};

const Verify2FA = () => {
    const {
        returnUrl: initialReturnUrl,
        rememberMe: initialRememberMe,
        provider: initialProvider,
    } = getTwoFaContextFromQuery();

    const [securityToken, setSecurityToken] = useState("");
    const [securityTokenDirty, setSecurityTokenDirty] = useState(false);
    const [rememberBrowser, setRememberBrowser] = useState(false);
    const [errors, setErrors] = useState([]);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [antiforgeryToken, setAntiforgeryToken] = useState("");
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const backToLoginState = (() => {
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl =
            initialReturnUrl ??
            urlParams.get("ReturnUrl") ??
            urlParams.get("returnUrl") ??
            "/";

        return {
            skipTwoFactorRedirect: true,
            returnUrl,
        };
    })();

    useEffect(() => {
        const tokenInput = document.querySelector(
            'input[name="__RequestVerificationToken"]'
        );
        setAntiforgeryToken(tokenInput ? tokenInput.value : "");
    }, []);

    useEffect(() => {
        setIsCheckingStatus(true);
        const checkUserStatus = async () => {
            try {
                const result = await request("/api/account/user-status", "GET");
                if (result.response && result.response.ok) {
                    const data = (result && result.data) || {};
                    if (!data.userId) {
                        const redirectUrl = checkForXSS(
                            new URLSearchParams(window.location.search).get(
                                "ReturnUrl"
                            ) || "/"
                        );
                        safeRedirect(redirectUrl);
                    } else {
                        setIsCheckingStatus(false);
                    }
                }
            } catch {
                setIsCheckingStatus(false);
                console.log(["Error checking user authentication status"]);
            }
        };

        checkUserStatus();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();

        setSubmitted(true);
        setErrors([]);

        if (!securityToken) {
            return;
        }

        setRequestInProgress(true);

        const urlParams = new URLSearchParams(window.location.search);

        const returnUrl =
            initialReturnUrl ??
            (urlParams.has("returnUrl") ? urlParams.get("returnUrl") : null);

        const rememberMe =
            initialRememberMe ??
            (urlParams.has("rememberMe") ? urlParams.get("rememberMe") : null);

        const provider =
            initialProvider ??
            (urlParams.has("provider") ? urlParams.get("provider") : null);

        const formData = {
            RememberBrowser: rememberBrowser,
            SecurityToken: securityToken,
        };

        if (returnUrl != null) formData.ReturnUrl = returnUrl;
        if (rememberMe != null) formData.RememberMe = rememberMe;
        if (provider != null) formData.Provider = provider;

        const submitVerify2FARequest = async () => {
            try {
                const result = await requestWithHeaders(
                    "/api/account/verify-2FA",
                    "POST",
                    formData,
                    {
                        "X-XSRF-TOKEN": antiforgeryToken,
                    },
                    false,
                    {
                        skipUnauthorizedRedirect: true,
                    }
                );

                if (result.response && result.response.ok) {
                    const redirectUrl = checkForXSS(
                        result.data.value?.returnUrl
                    );
                    safeRedirect(redirectUrl);
                } else {
                    const data =
                        (result && result.error && result.error.data) || {};
                    setSecurityToken("");
                    setSecurityTokenDirty(false);
                    setSubmitted(false);
                    setErrors(data.errors || ["Verification failed."]);
                    if (
                        data.errors &&
                        data.errors[0] ===
                            "Something unexpected has happened. Please try again or contact the administrator. you will be now redirected to login page in 5 seconds."
                    ) {
                        setTimeout(() => {
                            window.location.href = "/";
                        }, 5000);
                    }
                }
            } catch (error) {
                setSecurityToken("");
                setSecurityTokenDirty(false);
                setSubmitted(false);
                setErrors(["Verification failed."]);
            } finally {
                setRequestInProgress(false);
            }
        };

        submitVerify2FARequest();
    };

    if (isCheckingStatus) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Loading enabled={true} spinnerText="Loading..." />
            </div>
        );
    }

    return (
        <div className="login-page-wrapper">
            <div id="verify-2fa-block" className="login-block-container">
                <div className="login-block-header">
                    <img
                        className="logo"
                        src="/images/logo.svg"
                        alt="LiquidMetrix Logo"
                    />
                    <div className="errors">
                        {errors.length > 0 &&
                            errors.map((err, i) => <p key={i}>{err}</p>)}
                    </div>
                    <div className="description">
                        <span>
                            Please enter the security token that was sent to
                            your email address.
                        </span>
                    </div>
                </div>

                <div className="login-block-body">
                    <form
                        method="post"
                        autoComplete="off"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <div className="lm-input-group">
                            <input
                                type="password"
                                name="securityToken"
                                spellCheck={false}
                                autoComplete="off"
                                required
                                value={securityToken}
                                onChange={(e) => {
                                    setSecurityToken(e.target.value);
                                    setSecurityTokenDirty(true);
                                    if (errors.length > 0) setErrors([]);
                                }}
                                disabled={requestInProgress}
                                className={
                                    (securityTokenDirty || submitted) &&
                                    !securityToken
                                        ? "has-error"
                                        : ""
                                }
                            />
                            <span className="highlight"></span>
                            <span className="bar"></span>
                            <label htmlFor="securityToken">
                                Security Token
                            </label>
                            <span className="error">
                                {(securityTokenDirty || submitted) &&
                                    !securityToken &&
                                    "This field is required."}
                            </span>
                        </div>

                        <span className="actions">
                            <div className="lm-checkbox lm-checkbox-inline">
                                <input
                                    type="checkbox"
                                    id="remember-browser"
                                    checked={rememberBrowser}
                                    onChange={(e) =>
                                        setRememberBrowser(e.target.checked)
                                    }
                                    disabled={requestInProgress}
                                />
                                <label htmlFor="remember-browser">
                                    Remember this browser?
                                </label>
                            </div>
                            <div className="clearfix"></div>
                        </span>

                        <button
                            type="submit"
                            className={
                                "lm-btn primary sign-in-btn" +
                                (requestInProgress ? " loading" : "")
                            }
                            disabled={requestInProgress}
                        >
                            <span className="btn-label">Verify</span>
                            <LmLoader />
                        </button>
                    </form>
                </div>

                <div className="login-block-footer">
                    <Link
                        to={{
                            pathname: "/Account/Login",
                            state: backToLoginState,
                        }}
                        className="lm-link"
                    >
                        <span>Back to Sign In</span>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Verify2FA;
